import React from 'react'
import { Box, Typography, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'

type Ctx = {
  start: (lang: string) => void
}

const LangTransitionContext = React.createContext<Ctx>({ start: () => {} })

export const useLangTransition = () => React.useContext(LangTransitionContext)

export const LanguageTransitionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [active, setActive] = React.useState(false)
  const theme = useTheme()
  const { i18n } = useTranslation()

  React.useEffect(() => {
    const onChanged = () => {
      // Let content render in background then slide out
      setTimeout(() => setActive(false), 300)
    }
    i18n.on('languageChanged', onChanged)
    return () => { i18n.off('languageChanged', onChanged) }
  }, [i18n])

  const start = (lang: string) => {
    // Slide panels in
    setActive(true)
    // Change language shortly after animation begins
    setTimeout(() => { void i18n.changeLanguage(lang) }, 120)
  }

  const panelStyles = (side: 'left' | 'right') => ({
    position: 'fixed' as const,
    top: 0,
    bottom: 0,
    width: '50vw',
    [side]: 0,
    zIndex: 2000,
    background: theme.palette.mode === 'dark' ? '#000' : '#111',
    transform: active ? 'translateX(0)' : (side === 'left' ? 'translateX(-100%)' : 'translateX(100%)'),
    transition: 'transform 320ms ease',
    pointerEvents: 'none' as const
  })

  return (
    <LangTransitionContext.Provider value={{ start }}>
      {children}
      {/* Animated sliding panels */}
      <Box sx={panelStyles('left')} />
      <Box sx={panelStyles('right')} />
      <Box
        sx={{
          position: 'fixed', inset: 0, zIndex: 2001, display: 'flex', alignItems: 'center', justifyContent: 'center',
          pointerEvents: 'none',
          opacity: active ? 1 : 0,
          transition: 'opacity 160ms ease'
        }}
      >
        <Typography variant="subtitle1" sx={{ color: '#fff', letterSpacing: 1, textTransform: 'uppercase' }}>
          {i18n.t('loading')}
        </Typography>
      </Box>
    </LangTransitionContext.Provider>
  )
}

